import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { ShieldCheck, ShieldAlert, ShieldX } from "lucide-react";

const fmt = (n) => (n == null ? "—" : `$${Number(n).toLocaleString()}`);

// Phase D — pre-Pursue bid-fit card: this job's estimate vs WD's bonding limits + SBA size-standard headroom.
export const BidCapacityCard = ({ bidId }) => {
  const [fit, setFit] = useState(null);
  useEffect(() => {
    let live = true;
    api.get(`/bids/${bidId}/bid-fit`).then((r) => { if (live) setFit(r.data); }).catch(() => {});
    return () => { live = false; };
  }, [bidId]);
  if (!fit) return null;
  const c = fit.checks || {};
  const blocked = fit.hard_block;
  const tone = blocked ? "bg-red-50 border-red-300" : (fit.warnings?.length ? "bg-amber-50 border-amber-300" : "bg-emerald-50 border-emerald-300");
  const Icon = blocked ? ShieldX : (fit.warnings?.length ? ShieldAlert : ShieldCheck);
  const iconTone = blocked ? "text-red-600" : (fit.warnings?.length ? "text-amber-600" : "text-emerald-600");
  const label = blocked ? "OUTSIDE ENVELOPE" : (fit.warnings?.length ? "FITS — confirm" : "WITHIN ENVELOPE");
  return (
    <div className={`mt-4 rounded-lg border p-3.5 ${tone}`} data-testid="bid-capacity-card">
      <div className="flex items-center gap-2">
        <Icon className={`h-4 w-4 ${iconTone}`} />
        <span className="text-xs font-bold tracking-wide text-zinc-800" data-testid="bid-fit-label">{label}</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5 mt-2.5 text-[11px]">
        <Row label="Evaluated magnitude" v={fmt(c.estimated_magnitude)} strong
             over={(c.single_job_limit != null && c.estimated_magnitude > c.single_job_limit)} />
        <Row label="Gov magnitude (DFARS 236.204)" v={fmt(c.gov_magnitude_floor)} title={c.gov_magnitude_note} />
        <Row label="Priced total (Stage 4)" v={fmt(c.priced_total)} />
        <Row label="Single-job bond limit" v={fmt(c.single_job_limit)} over={c.single_job_limit != null && c.estimated_magnitude > c.single_job_limit} />
        <Row label="Aggregate bond limit" v={fmt(c.aggregate_limit)} over={c.aggregate_limit != null && c.estimated_magnitude > c.aggregate_limit} />
        <Row label="Largest completed" v={fmt(c.largest_completed_project)} />
        <Row label="SBA size standard" v={fmt(c.sba_size_standard)} />
        <Row label="NAICS" v={c.naics || "—"} />
      </div>
      {c.gov_magnitude_floor != null && (
        <div className="mt-1.5 text-[10px] text-zinc-500" data-testid="bid-fit-magnitude-source">
          Magnitude = max(DFARS floor, priced total) — the higher of the government's disclosed size and WD's priced bid.
        </div>
      )}
      {blocked && (
        <ul className="mt-2 list-disc pl-4 text-[11px] text-red-700 space-y-0.5" data-testid="bid-fit-reasons">
          {(fit.reasons || []).map((r, i) => <li key={i}>{r}</li>)}
        </ul>
      )}
      {!!fit.warnings?.length && (
        <ul className="mt-2 list-disc pl-4 text-[11px] text-amber-700 space-y-0.5" data-testid="bid-fit-warnings">
          {fit.warnings.map((w, i) => <li key={i}>{w}</li>)}
        </ul>
      )}
    </div>
  );
};

const Row = ({ label, v, over, strong, title }) => (
  <div title={title || undefined}>
    <div className="text-[9px] uppercase tracking-wider text-zinc-400">{label}</div>
    <div className={`font-mono ${over ? "text-red-600 font-bold" : (strong ? "text-zinc-900 font-bold" : "text-zinc-800")}`}>{v}</div>
  </div>
);
